import React, { useState } from 'react';
import { Check, Loader2, Pencil, TriangleAlert } from 'lucide-react';
type ReviewConfidence = 'HIGH' | 'MEDIUM' | 'LOW';
type ReviewRow = {
  written: string;
  resolved: string;
  quantity: string;
  weight: string;
  confidence: ReviewConfidence;
  flag?: string;
};
const reviewRows: ReviewRow[] = [{
  written: 'Orange Fruit Juice Powder',
  resolved: 'Compound — 5 sub-ingredients',
  quantity: '3.068 g',
  weight: '51.133%',
  confidence: 'HIGH'
}, {
  written: 'Soluble Corn Fiber (Fibersol-2 #1000901)',
  resolved: 'Resistant maltodextrin',
  quantity: '2.057 g',
  weight: '34.291%',
  confidence: 'MEDIUM',
  flag: 'Trade name matched to resistant maltodextrin. Confirm the resolution.'
}, {
  written: 'Vitamin C (as ascorbic acid)',
  resolved: 'Ascorbic acid',
  quantity: '0.700 g',
  weight: '11.667%',
  confidence: 'HIGH'
}, {
  written: 'Silicon Dioxide',
  resolved: 'Silicon dioxide',
  quantity: '0.060 g',
  weight: '1.000%',
  confidence: 'HIGH'
}, {
  written: 'Zinc (as zinc sulfate)',
  resolved: 'Zinc sulfate',
  quantity: '0.040 g',
  weight: '0.667%',
  confidence: 'HIGH'
}, {
  written: 'Stevia Extract (RA60%-95%)',
  resolved: 'Stevia rebaudiana',
  quantity: '0.030 g',
  weight: '0.500%',
  confidence: 'HIGH'
}, {
  written: 'Vitamin A (retinyl acetate, 500,000 IU/g)',
  resolved: 'Retinol acetate',
  quantity: '0.024 g',
  weight: '0.400%',
  confidence: 'HIGH'
}, {
  written: 'Canthaxanthin (as Lucarotin 1 CWD/Y)',
  resolved: 'Canthaxanthin',
  quantity: '0.020 g',
  weight: '0.325%',
  confidence: 'LOW',
  flag: 'Proprietary grade. Confirm whether this is canthaxanthin or beta-carotene — the two differ in vitamin A activity.'
}];
export function FormulaProcessingCard({
  message
}: {
  message: string;
}) {
  return <div className="flex min-h-[320px] items-center justify-center" data-id="element-512" data-mp="1u8pjdq3gua97">
      <div className="flex flex-col items-center gap-4 rounded-2xl border border-[#dce3da] bg-white px-10 py-12 text-center shadow-sm" data-id="element-513" data-mp="dr1t1ouh2ebg">
        <Loader2 className="h-8 w-8 animate-spin text-forest-green" data-id="element-514" data-mp="2kgvntpf715hj" />
        <p className="text-sm font-semibold text-evergreen" data-id="element-515" data-mp="2kg3rgcdrv3q7">{message}</p>
      </div>
    </div>;
}
interface FormulaReviewProps {
  onCancel: () => void;
  onConfirm: () => void;
}
export function FormulaReview({
  onCancel,
  onConfirm
}: FormulaReviewProps) {
  const [resolutions, setResolutions] = useState<Record<string, string>>(() => Object.fromEntries(reviewRows.map(row => [row.written, row.resolved])));
  return <div className="space-y-5" data-id="element-516" data-mp="1wratybhi4nth">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-517" data-mp="1wr19f3f878v6">
        <div data-id="element-518" data-mp="1rrg1i3i2vq6b">
          <h2 className="font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-519" data-mp="1rt98jjwlsceu">Review extracted ingredients</h2>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-[#6b7d70]" data-id="element-520" data-mp="1ns784zqy9uba">Check the resolution before we assess. Correct anything we&apos;ve read wrongly.</p>
        </div>
        <div className="flex shrink-0 items-center gap-3" data-id="element-521" data-mp="3i4dw6z4y05l0">
          <button type="button" onClick={onCancel} className="text-sm font-semibold text-[#617366] transition hover:text-evergreen hover:underline" data-id="element-522" data-mp="3i2sxqyfic2hv">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} className="inline-flex items-center gap-2 rounded-xl bg-forest-green px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-evergreen" data-id="element-523" data-mp="18tuy7f0bvahl">
            Confirm and analyse
          </button>
        </div>
      </div>

      <p className="font-mono text-xs font-semibold uppercase tracking-[0.11em] text-[#6f8174]" data-id="element-524" data-mp="pwtv5zqc3kga">
        8 ingredients extracted · 6 resolved with high confidence · 2 need confirmation
      </p>

      <section className="overflow-hidden rounded-2xl border border-[#dce3da] bg-white shadow-sm" data-id="element-525" data-mp="yxjmor8kaoo7">
        <div className="overflow-x-auto" data-id="element-526" data-mp="25e3xqk7ibbuz">
          <table className="w-full min-w-[860px] text-left" data-id="element-527" data-mp="25dc3bcpnbk6a">
            <thead className="border-b border-[#e5ebe3] bg-[#fbfdf9]" data-id="element-528" data-mp="2acf8qy9jfses">
              <tr className="font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-[#758578]" data-id="element-529" data-mp="2aerpx97h1yqs">
                <th className="px-5 py-3" data-id="element-530" data-mp="23woloxoai7x4">Ingredient (as written)</th>
                <th className="px-5 py-3" data-id="element-531" data-mp="23yj7sfbas62s">Resolved to</th>
                <th className="px-5 py-3" data-id="element-532" data-mp="2hwpsetr8k9ki">Quantity</th>
                <th className="px-5 py-3" data-id="element-533" data-mp="2hu4rq82obzoc">% by weight</th>
                <th className="px-5 py-3 text-right" data-id="element-534" data-mp="2du2zepitwilq">Confidence</th>
                <th className="px-5 py-3 text-right" data-id="element-535" data-mp="2ru3yq0jg3hz3">
                  <span className="sr-only" data-id="element-536" data-mp="2rssszv6fw05c">Row action</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#edf0eb]" data-id="element-537" data-mp="2mtpz96vwtgco">
              {reviewRows.map(row => <React.Fragment key={row.written} data-id="element-538" data-mp="2wldn6xt9cf9e">
                  <tr data-id="element-539" data-mp="2wmoqyxmpdn0i">
                    <td className="px-5 py-4 text-sm font-semibold text-evergreen" data-id="element-540" data-mp="2wm5gu33ao2x1">{row.written}</td>
                    <td className="px-5 py-4" data-id="element-541" data-mp="3aj28g6r8loez">
                      <label className="relative block" data-id="element-542" data-mp="35mk4h93qdu3p">
                        <span className="sr-only" data-id="element-543" data-mp="35jz4yqqwnq94">Resolved name for {row.written}</span>
                        <input value={resolutions[row.written]} onChange={event => setResolutions(current => ({
                      ...current,
                      [row.written]: event.target.value
                    }))} className="w-full min-w-[190px] rounded-lg border border-[#d7e0d4] py-2 pl-3 pr-8 text-sm text-evergreen outline-none focus:border-forest-green focus:ring-2 focus:ring-forest-green/15" data-id="element-544" data-mp="3p8n1lkqb0440" />
                        <Pencil className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#9aa79c]" data-id="element-545" data-mp="3b8caqhozupiu" />
                      </label>
                    </td>
                    <td className="px-5 py-4 font-mono text-sm text-[#56685c]" data-id="element-546" data-mp="31hq43cz999ca">{row.quantity}</td>
                    <td className="px-5 py-4 font-mono text-sm text-[#56685c]" data-id="element-547" data-mp="2nhyj2sabmsos">{row.weight}</td>
                    <td className="px-5 py-4 text-right" data-id="element-548" data-mp="2si2zkib8bnf0">
                      <ReviewConfidenceTag level={row.confidence} data-id="element-549" data-mp="2sjnui91gh8de" />
                    </td>
                    <td className="px-5 py-4 text-right" data-id="element-550" data-mp="y2nu8pvw21wi">
                      <button type="button" className="rounded-lg border border-[#d6dfd3] px-2.5 py-1 text-xs font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-551" data-mp="tae5hd6if7vt">
                        Remove
                      </button>
                    </td>
                  </tr>
                  {row.flag && <tr data-id="element-552" data-mp="1gz5phh9y4jxi">
                      <td colSpan={6} className="px-5 pb-4" data-id="element-553" data-mp="1gzfi7cmdl56w">
                        <p className="ml-4 inline-flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900" data-id="element-554" data-mp="1c0vtj4gedfau">
                          <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" data-id="element-555" data-mp="130pmo1f7fdkn" />
                          {row.flag}
                        </p>
                      </td>
                    </tr>}
                </React.Fragment>)}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-2 border-t border-[#e5ebe3] bg-[#fbfdf9] px-5 py-3" data-id="element-556" data-mp="239p1lfba15u7">
          <Check className="h-4 w-4 text-forest-green" data-id="element-557" data-mp="288s8l5athm4t" />
          <span className="font-mono text-xs font-semibold text-[#56685c]" data-id="element-558" data-mp="2c9c464fqshou">Total: 6.000 g · 100.000%</span>
        </div>
      </section>
    </div>;
}
function ReviewConfidenceTag({
  level
}: {
  level: ReviewConfidence;
}) {
  const style: Record<ReviewConfidence, string> = {
    HIGH: 'text-[#8b968d]',
    MEDIUM: 'text-amber-700',
    LOW: 'text-red-700'
  };
  return <span className={`font-mono text-[10px] font-semibold uppercase tracking-[0.13em] ${style[level]}`} data-id="element-559" data-mp="2qwney443nxnw">{level}</span>;
}